import React, { useCallback, useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useParams, useHistory, useLocation } from "react-router-dom";

import BasePadding from "@cayuse/shared-react.components.base-padding";
import Breadcrumbs from "@cayuse/shared-react.components.breadcrumbs";
import LoadingIndicator from "@cayuse/shared-react.components.loading-indicator";
import SectionHeader from "@cayuse/shared-react.components.section-header";
import VerticalSpacing from "@cayuse/shared-react.components.vertical-spacing";
import { Ref } from "semantic-ui-react";
import { validate as uuidValidate } from "uuid";

import Tabs from "components/Tab/Tabs";
import SubawardFormView from "containers/Subaward/Detail/Form";
import SubawardNotesView from "containers/Subaward/Detail/Notes";
import SubawardTasksView from "containers/Subaward/Detail/Tasks";
import { AWARDS_BASE_ROUTE, PROJECTS_BASE_ROUTE, SUBAWARDS_BASE_ROUTE } from "globals/constants";
import { useDictionary, useFormAccesses } from "hooks";
import { getSubaward, resetSubaward } from "store/actions/subaward";
import { subawardSelectors, tenantSelectors } from "store/selectors";
import { spUrl } from "utils";

import SPLink from "../../../components/SPLink";
import { useProtectedAction } from "../../../hooks/useProtectedAction";
import { resetForm } from "../../../store/actions/form";
import SubawardSummary from "../Summary/SubawardSummary";
import SubawardAttachmentsView from "./Attachments/SubawardAttachmentsView";
import SubawardHistoryView from "./History";
import styles from "./styles.css";


const SubawardDetailView = () => {
	const { isRefresh, isFetching: isSubawardLoading, subaward } = useSelector(subawardSelectors.content);

	const [ justActivatedSectionLabel, setJustActivatedSectionLabel ] = useState("");
	const [ isFormComplete, setIsFormComplete ] = useState(false);
	const [ formVariables, setFormVariables ] = useState({});
	const [ isFormLoading, setIsFormLoading ] = useState(false); // todo: get from redux
	const [ isUploading, setIsUploading ] = useState(false);
	const dictionary = useDictionary("subawards.subawardDetails");
	const tabsRef = useRef(null);
	const { awardId } = useParams();
	const { subawardId } = useParams();
	const { search } = useLocation();
	const history = useHistory();
	const awardFormAccesses = useFormAccesses("award", { checkParentAccess: true });
	const tenant = useSelector(tenantSelectors.tenant);
	const projectLevelPermissionsEnabled = tenant?.projectLevelPermissionsEnabled ?? true;
	const hasAccessToViewParentAward = awardFormAccesses.view || projectLevelPermissionsEnabled;

	useEffect(() => {
		return () => {
			resetForm();
			resetSubaward();
		};
	}, []);

	useEffect(() => {
		const tab = new URLSearchParams(search).get("tab");
		if(tab === "tasks" && !subaward.id) {
			getSubaward(subawardId);
		}
	}, [ search ]);

	useEffect(() => {
		resetSubaward();
		if (awardId && !uuidValidate(awardId)) {
			history.push(spUrl(AWARDS_BASE_ROUTE));
		}

		if (!uuidValidate(subawardId)) {
			history.push(spUrl(SUBAWARDS_BASE_ROUTE));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ awardId, subawardId ]);

	const handleAwardBreadcrumbClick = useProtectedAction({
		hasAccess: hasAccessToViewParentAward,
		errorMessage: dictionary.errors.noAwardViewAccess
			|| "You do not have permission to view the parent award. " +
			"Please contact an administrator to check your permissions."
	});

	const renderAwardBreadcrumb = () => (
		<SPLink
			to={hasAccessToViewParentAward ? `${AWARDS_BASE_ROUTE}/${subaward?.awardId}` : "#"}
			onClick={handleAwardBreadcrumbClick}
		>
			{subaward?.awardNumber}
		</SPLink>
	);

	const breadcrumbsFromAward = (
		<Breadcrumbs breadcrumbs={[
			{
				key: 0,
				text: dictionary?.breadcrumbRoot || "My Active Awards",
				link: spUrl(AWARDS_BASE_ROUTE),
			},
			{
				key: 1,
				text: subaward?.projectTitle,
				link: spUrl(`${PROJECTS_BASE_ROUTE}/${subaward?.projectId}`),
			},
			{
				key: 2,
				text: renderAwardBreadcrumb()
			},
			{
				key: 3,
				text: subaward?.subawardNumber?.replace(`${subaward?.awardNumber}-`, "")
			}
		]}
		/>
	);

	const breadcrumbsFromProject = (
		<Breadcrumbs breadcrumbs={[
			{
				key: 0,
				text: dictionary?.breadcrumbProjectRoot || "My Active Subawards",
				link: spUrl(SUBAWARDS_BASE_ROUTE),
			},
			{
				key: 1,
				text: subaward?.projectTitle,
				link: spUrl(`${PROJECTS_BASE_ROUTE}/${subaward?.projectId}`),
			},
			{
				key: 2,
				text: subaward?.subawardNumber?.replace(`${subaward?.projectNumber}-`, "")
			}
		]}
		/>
	);

	const handleFormLoaded = useCallback(formCompleted => {
		setIsFormLoading(false);
		setIsFormComplete(formCompleted);
	}, []);

	const panes = [ // todo: remember which tab was open
		{
			menuItem: dictionary.tabLabels?.form || "Subaward Form",
			render: () => (
				<div loading={isFormLoading}>
					<SubawardFormView
						isFormComplete={isFormComplete}
						setIsFormComplete={setIsFormComplete}
						handleFormLoaded={handleFormLoaded}
						setFormVariables={setFormVariables}
						formVariables={formVariables}
						justActivatedSectionLabel={justActivatedSectionLabel}
						setJustActivatedSectionLabel={setJustActivatedSectionLabel}
					/>
				</div>
			),
			ref: useRef(null)
		},
		{
			menuItem: dictionary.tabLabels?.history || "Subaward History",
			render: () => <SubawardHistoryView />,
			ref: useRef(null)
		},
		{
			menuItem: dictionary.tabLabels?.tasks || "Tasks",
			render: () => <SubawardTasksView />,
			ref: useRef(null)
		},
		{
			menuItem: dictionary.tabLabels?.notes || "Notes",
			render: () => <SubawardNotesView />,
			ref: useRef(null)
		},
		{
			menuItem: dictionary.tabLabels?.attachments || "Attachments",
			render: () => (
				<SubawardAttachmentsView
					tabsRef={tabsRef}
					formTabText={dictionary.tabLabels?.form}
					setJustActivatedSectionLabel={setJustActivatedSectionLabel}
					setIsUploading={setIsUploading}
					isUploading={isUploading}
				/>
			),
			ref: useRef(null)
		}
	];

	const breadcrumbs = subaward?.awardId ? breadcrumbsFromAward : breadcrumbsFromProject;

	return (
		<div>
			<div className={styles.sectionHeaderWrapper}>
				{isSubawardLoading && !isRefresh && (
					<div className={styles.sectionHeaderLoader}>
						<LoadingIndicator/>
					</div>
				)}
				<SectionHeader header={subaward.projectTitle} breadcrumbs={breadcrumbs} />
			</div>

			<BasePadding>
				<VerticalSpacing>
					<SubawardSummary isFormComplete={isFormComplete} />
				</VerticalSpacing>

				<Ref innerRef={tabsRef}>
					<Tabs
						className={styles.detailsTabs}
						panes={panes}
					/>
				</Ref>
			</BasePadding>
		</div>
	);
};

export default SubawardDetailView;
